'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentProps, createContext, useContext } from 'react';
import { QueryApi } from '@/features/query/api';
import { getFormatedPhoneNumber } from '@/shared/lib/getFormatedPhoneNumber';
import { Button } from '@/shared/ui/button';
import { Icon as IconCmp } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';

type VenueTelephoneActionContextValue = {
    telephone: ReturnType<typeof getFormatedPhoneNumber> | null;
};

const VenueTelephoneActionContext = createContext<VenueTelephoneActionContextValue>({ telephone: null });

type VenueTelephoneActionWrapperProps = ComponentProps<typeof Layout>;

export const VenueTelephoneActionWrapper = ({
    children,
    media,
    ...props
}: VenueTelephoneActionWrapperProps) => {
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const telephoneData =
        venueQuery.data?.contactInfo?.telephonePrefix && venueQuery.data.contactInfo.telephone
            ? getFormatedPhoneNumber(
                  venueQuery.data.contactInfo.telephonePrefix,
                  venueQuery.data.contactInfo.telephone,
              )
            : null;

    if (!telephoneData) {
        return null;
    }

    return (
        <VenueTelephoneActionContext.Provider value={{ telephone: telephoneData }}>
            <Layout media={media} {...props}>
                {children}
            </Layout>
        </VenueTelephoneActionContext.Provider>
    );
};

type VenueTelephoneActionIconProps = ComponentProps<typeof Layout>;

export const VenueTelephoneActionIcon = ({ media, ...props }: VenueTelephoneActionIconProps) => {
    return (
        <Layout
            media={{
                ...media,
                initial: {
                    width: 18,
                    height: 18,
                    ...media?.initial,
                },
            }}
            {...props}
        >
            <IconCmp name="share" data-testid="contact-info-action-icon" width={18} height={18} />
        </Layout>
    );
};

type VenueTelephoneActionTextProps = ComponentProps<typeof Layout>;

export const VenueTelephoneActionText = ({ media, ...props }: VenueTelephoneActionTextProps) => {
    const { telephone } = useContext(VenueTelephoneActionContext);
    return (
        <Layout element="span" media={media} data-testid="contact-info-box-telephone" {...props}>
            {telephone?.formated}
        </Layout>
    );
};

type VenueTelephoneActionButtonProps = ComponentProps<typeof Layout>;

export const VenueTelephoneActionButton = ({
    children,
    media,
    ...props
}: VenueTelephoneActionButtonProps) => {
    const t = useTranslations();
    const { telephone } = useContext(VenueTelephoneActionContext);
    return (
        <Layout media={media} {...props}>
            <Button size="small" asChild data-testid="contact-info-action-button">
                {children ?? (
                    <a href={telephone?.href} data-testid="contact-info-box-call-button">
                        {t('WORDS.CALL')}
                    </a>
                )}
            </Button>
        </Layout>
    );
};

// Compound component structure
export const VenueTelephoneActionCompound: typeof VenueTelephoneActionWrapper & {
    Icon: typeof VenueTelephoneActionIcon;
    Text: typeof VenueTelephoneActionText;
    Button: typeof VenueTelephoneActionButton;
} = Object.assign(VenueTelephoneActionWrapper, {
    Icon: VenueTelephoneActionIcon,
    Text: VenueTelephoneActionText,
    Button: VenueTelephoneActionButton,
});
